// Is this user someone who actually records encounters? Used to scope
// chart-level nudges (status chip, "no recording" banner, leave-chart confirm)
// to the people for whom this is a real workflow — admin staff, schedulers and
// chart reviewers shouldn't be nagged.
//
// care.brainymed hardcoded a username allowlist for ITS practice (9928), which
// is meaningless on any other practice and silently disables the nudge for a
// newly provisioned one. Derive it instead: a user linked to a provider record
// (providerid > 0) is a recording provider. Admin/scheduler logins carry
// providerid 0, so they're excluded automatically — no per-practice list to
// maintain, and a freshly provisioned practice behaves correctly on day one.
//
// Accepts the /auth/me user object (redux state.userconfig). A bare string is
// rejected on purpose: a username alone can't answer this question generically.
export function isRecordingProvider(user) {
  if (!user || typeof user !== "object") return false;
  const pid = Number(user.providerid ?? user.provider_id ?? 0);
  return Number.isFinite(pid) && pid > 0;
}

// Compares an appointment's start date (any parseable form) to today in the
// browser's local timezone. We only nudge for TODAY's appointments — prior
// visits and future ones don't need recording reminders.
export function isAppointmentToday(startdate) {
  if (!startdate) return false;
  try {
    const d = new Date(startdate);
    if (isNaN(d.getTime())) return false;
    const today = new Date();
    return (
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate()
    );
  } catch (_) {
    return false;
  }
}

// Per-appointment dismiss key for the 90s nag banner. Once a provider clicks
// "Dismiss for this visit" we remember it so the same banner doesn't fire
// again every time they switch tabs / navigate within the chart.
export function nagDismissedKey(patientid, appointmentid) {
  return `chart-nag-dismissed:${patientid || "?"}:${appointmentid || "noappt"}`;
}

export function isNagDismissed(patientid, appointmentid) {
  try {
    return localStorage.getItem(nagDismissedKey(patientid, appointmentid)) === "1";
  } catch (_) {
    return false;
  }
}

export function markNagDismissed(patientid, appointmentid) {
  try {
    localStorage.setItem(nagDismissedKey(patientid, appointmentid), "1");
  } catch (_) { /* non-fatal */ }
}

// Format MM:SS for the elapsed-time chip. Mirrors RecordingContext output.
export function formatElapsed(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(Math.floor(s % 60)).padStart(2, "0");
  return `${mm}:${ss}`;
}
